/**
 * A FRESH CODE, AS THIS TAB KNOWS OF IT (round four, slice M3, `docs/seam.md` P59). A press behind a
 * fresh code, her tick on the bank or the card, and every card press (`components/StripeWay.tsx`),
 * goes to the door that asks for one unless a code was typed in the last fifteen minutes (CLAUDE.md
 * binding rule 5). The replay of the owner's walk found two things that read as broken:
 *
 * - "ONE MOMENT", THEN THE CODE STEP. The press drew the panel over the page, and half a second
 *   later the door's `403 step_up_required` undimmed it into the code step. So the tab knows until
 *   when a fresh code holds: as `/api/me` says it on every page (`Entry`), fifteen minutes from a
 *   code typed here, by this clock, and none once the door asks for one. A press with none standing
 *   draws the code step at once and asks the door nothing; with one standing it draws the panel, as
 *   before. It counts as standing only while more than half a minute of it is left, so a press that
 *   reaches the door a little late, or a clock a little out of step with the Worker's, draws the code
 *   step rather than a panel the door then takes away. The door still decides: its 403 still leads
 *   to the code step, and says none stands.
 * - EVERY TAB OF HERS ON THE ONE SESSION. The session is the cookie's, which every tab of her browser
 *   shares, so a code typed in one tab, or a sign-in in another, stamps the session her other open
 *   tab is on too; a tab that knew only its own load and its own codes drew the code step there, and
 *   emailed a new code, while the code she had just typed still held at the door. So the instant,
 *   and nothing else, is kept in the `localStorage` her tabs share, written as any tab learns or says
 *   it and read at every press; where the browser will not keep it, the tab goes by what it knows
 *   itself, as before. The instant is no key: a wrong one draws a panel the door's 403 takes away,
 *   or a code step the door would not have asked for, and nothing more.
 * - A SECOND CODE STEP, A SECOND EMAIL. Each code step asked for a code as it was drawn, and only
 *   the newest code works (`api/src/db/codes.ts`), so a second step within minutes of the first,
 *   after "I can't pay by bank" or a reload while she was in her mail, stopped the code she may
 *   already have open from working. So the tab keeps when a code was last asked for, and the id the
 *   Worker named that ask by, in its own `sessionStorage`, which a reload keeps and another tab never
 *   reads, and a code step drawn within ten minutes of it asks for none; "Send a new code" stays
 *   hers (`components/FreshCode.tsx`). A code that worked forgets it, since that code is spent, and
 *   so does a sign-in, whose session has its own codes (`screens/SignIn.tsx`).
 * - AND NEVER "ALREADY ON ITS WAY" FOR A CODE THAT WAS NOT SENT (the review of 303dfc2, the owner's
 *   word of 2026-10-20, never silent). The time alone let a step drawn again say a code was already
 *   on its way when Microsoft or the phone system had not taken it. With the ask's id kept beside
 *   it, that step asks what came of the send first, and a send that failed is forgotten, by its id,
 *   so the next step asks for a code afresh. Where the Worker named no ask, the time alone is kept,
 *   as before.
 *
 * Every read and write of the browser's storage is inside a try: a browser that keeps nothing asks
 * for a code on every step, as before, and nothing breaks. Nothing here says a code was sent (binding
 * rule 3), and nothing here is a decision: the door's answer is the one acted on.
 */
import { ASK_ID } from '../api.ts';

/** How long a code typed counts at the door: fifteen minutes (`STEP_UP_WINDOW_MS`, the Worker's). */
export const FRESH_FOR_MS = 15 * 60 * 1000;

/** A fresh code counts as standing only while more than this is left of it: half a minute. */
export const FRESH_MARGIN_MS = 30 * 1000;

/** A code step drawn within this of the last code the tab asked for asks for none: ten minutes. */
export const ASKED_LATELY_MS = 10 * 60 * 1000;

/**
 * When this tab last asked for a fresh code, in milliseconds, and after a space the id the Worker
 * named the ask by where it named one, in its own `sessionStorage`.
 */
export const CODE_ASKED_AT = 'home.freshCode.askedAt';

/** A code the tab asked for lately: the id the Worker named the ask by, or null where it named none. */
export interface AskKept {
  readonly ask: string | null;
}

/** Until when her session's fresh code holds, in milliseconds, in the `localStorage` her tabs share. */
export const FRESH_UNTIL = 'home.freshCode.until';

/**
 * Until when a fresh code holds, in milliseconds, or null where none stands, as this tab last
 * learned or said it: what it goes by where the browser will not keep the instant for every tab.
 */
let until: number | null = null;

/** Whether the browser's storage holds what this tab last learned or said, so every tab reads it. */
let shared = true;

/** The storage every tab of her browser shares, which a missing one throws on. */
function browser(): Storage {
  return window.localStorage;
}

/** What a fresh code holds until, as this tab learned or said it: for this tab, and every tab. */
function know(held: number | null): void {
  until = held;
  try {
    if (held === null) browser().removeItem(FRESH_UNTIL);
    else browser().setItem(FRESH_UNTIL, String(held));
    shared = true;
  } catch {
    // Kept nothing for the other tabs: this one goes by what it knows itself.
    shared = false;
  }
}

/**
 * Until when a fresh code holds, as her browser knows it: what any tab of hers last learned or said,
 * where the browser keeps it, and this tab's own otherwise. Anything this file did not write is none.
 */
function known(): number | null {
  if (!shared) return until;
  let kept: string | null;
  try {
    kept = browser().getItem(FRESH_UNTIL);
  } catch {
    return until;
  }
  const held = kept === null || kept === '' ? NaN : Number(kept);
  return Number.isSafeInteger(held) ? held : null;
}

/** Whether a fresh code holding until `held` stands at `now`: more than half a minute of it left. */
export function standsAt(held: number | null, now: number): boolean {
  return held !== null && held - now > FRESH_MARGIN_MS;
}

/**
 * Whether a code asked for at `at` was asked for lately at `now`: within ten minutes, and never
 * after now, which only a clock set back says, and then nothing says when it was.
 */
export function askedLatelyAt(at: number | null, now: number): boolean {
  if (at === null) return false;
  const since = now - at;
  return since >= 0 && since < ASKED_LATELY_MS;
}

/** What `/api/me` said: until when this session's fresh code holds, or nothing, where none stands. */
export function heardFresh(held: number | undefined): void {
  know(held ?? null);
}

/**
 * A code typed here worked: the Worker stamped the session as it took it, so a fresh code holds for
 * fifteen minutes from now, by this clock, in every tab of hers; and the code asked for is spent.
 */
export function codeWorked(now: number = Date.now()): void {
  know(now + FRESH_FOR_MS);
  forgetCodeAsked();
}

/** The door asked for a fresh code: none stands, in this tab or any other of hers. */
export function noFreshCode(): void {
  know(null);
}

/**
 * Whether a fresh code stands now, as far as her browser knows: a press behind a code goes to the
 * door. A code typed in her other open tab counts here, since every tab is on the one session.
 */
export function freshStands(now: number = Date.now()): boolean {
  return standsAt(known(), now);
}

/** The tab's own storage, or null where the browser gives none. */
function tab(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/**
 * A code was asked for now, and the Worker named the ask `ask`, or nothing: the time is kept, with
 * the id beside it where there is one, in the tab's own storage.
 */
export function noteCodeAsked(ask: string | null, now: number = Date.now()): void {
  try {
    tab()?.setItem(CODE_ASKED_AT, ask === null ? String(now) : `${now} ${ask}`);
  } catch {
    // Kept nothing: the next code step asks for a code, as before.
  }
}

/** What the tab keeps of its last ask, read: when, and its id or none; null for anything else. */
function keptAsk(): { readonly at: number; readonly ask: string | null } | null {
  let kept: string | null;
  try {
    kept = tab()?.getItem(CODE_ASKED_AT) ?? null;
  } catch {
    return null;
  }
  if (kept === null) return null;
  const [time = '', ask, ...rest] = kept.split(' ');
  if (rest.length > 0 || (ask !== undefined && !ASK_ID.test(ask))) return null;
  const at = time === '' ? NaN : Number(time);
  return Number.isSafeInteger(at) ? { at, ask: ask ?? null } : null;
}

/**
 * The code the tab asked for lately, or null for none: a code step drawn now asks for none, and asks
 * what came of that one's send by its id first, where the Worker named one.
 */
export function codeAskedLately(now: number = Date.now()): AskKept | null {
  const kept = keptAsk();
  return kept !== null && askedLatelyAt(kept.at, now) ? { ask: kept.ask } : null;
}

/**
 * Forgets when a code was last asked for: a code that worked is spent, and a sign-in has its own.
 * Given an ask's id, it forgets only that ask, where it is the one kept: its send failed, so no code
 * of it is on its way, and a later ask's stays.
 */
export function forgetCodeAsked(ask?: string): void {
  try {
    if (ask !== undefined && keptAsk()?.ask !== ask) return;
    tab()?.removeItem(CODE_ASKED_AT);
  } catch {
    // Nothing kept to forget.
  }
}
